import { Card, Divider, Grid, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { WSIcons } from "../../commonFunctions/iIcons";
import WSButton from "../../Components/Button/WSButton";
import WSDateField from "../../Components/DateField/WSDateField";
import WSTextField, {
  CurrencyType,
  TextType
} from "../../Components/TextBox/WSTextBox";
import config from "../../Config/clientConfig.json";

import dayjs from "dayjs";
import {
  callAPI,
  clearRequest,
  Method
} from "../../commonFunctions/CommonFunctions";
import { HeaderList, IHeaderListActions } from "../../Components/Header/HeaderList";
import WSPhone from "../../Components/Phone/WSPhone";
import WSSelectField from "../../Components/Select/WSSelect";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import WSTextAreaField from "../../Components/WSTextArea/WSTextArea";

export const Orders: React.FC<any> = React.memo((props) => {
  const { id, orderId, quatationId } = useParams();
  const [loading, setLoading] = useState(false)
  const { control, register, handleSubmit, setValue } = useForm({
    defaultValues: {
      ownerId: localStorage.getItem("UserId"),
      customerName: "",
      phone: "",
      stage: "",
      completion: "",
      commencement: "",
      address: "",
      leadId: "",
      date: "",
      termsAndConditions: "",
      paymentTerms: "",
      remarks: "",
      approximateArea: "",
      rate: "",

      items: [{ productId: "", quantity: "", unit: "" }]
    }
  });
  const { fields, append, remove, replace } = useFieldArray({
    name: "items",
    control
  });
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();

  const [products, setProducts] = useState<any>([]);
  const [lead, setLead] = useState<any>([]);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response: any = await callAPI("products", Method.GET);
        if (response) {
          setProducts(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    fetchProducts();
  }, []);

  useEffect(() => {
    const fetchLeads = async () => {
      try {
        const response: any = await callAPI("leads", Method.GET);
        if (response) {
          setLead(response);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    fetchLeads();
  }, []);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response: any = await callAPI(`workorders/${orderId}`, Method.GET);
        if (response?.data) {
          const orderData = response.data;
          setValue("ownerId", orderData.ownerId || "");
          setValue("customerName", orderData.customerName || "");
          setValue("address", orderData.address || "");
          setValue("phone", orderData.phone || "");
          setValue("stage", orderData.stage || "Draft");
          setValue("approximateArea", orderData.approximateArea || "");
          setValue("termsAndConditions", orderData.termsAndConditions || "");
          setValue("paymentTerms", orderData.paymentTerms || "");
          setValue("remarks", orderData.remarks || "");
          setValue("leadId", orderData.leadId || "");
          setValue("rate", orderData.rate || "");
          // Format Dates
          setValue("date", dayjs(orderData.date).format("MM-DD-YYYY"));
          setValue(
            "commencement",
            dayjs(orderData.commencement).format("MM-DD-YYYY")
          );
          setValue(
            "completion",
            dayjs(orderData.completion).format("MM-DD-YYYY")
          );

          // Populate Items if available
          if (orderData.quotationItems?.length) {
            const formattedItems = orderData.quotationItems.map(
              (item: any) => ({
                productId: item.productId || "",
                quantity: item.quantity,
                unit: item.unit,
                // unitPrice: item.unitPrice
              })
            );
            replace(formattedItems); // Updates useFieldArray fields
          }
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    if (orderId) fetchOrder();
  }, [orderId, setValue, replace]);
  useEffect(() => {

    const fetchQuotation = async () => {
      try {
        const response: any = await callAPI("quotations", Method.GET, {}, quatationId);
        if (response?.data) {
          // Set main form values
          setValue("ownerId", response.data.ownerId);
          setValue("customerName", response.data.customerName);
          setValue("address", response.data.address);
          // setValue("subject", response.data.subject);

          //  setValue("discount", response.data.discount);
          setValue("termsAndConditions", response.data.termsAndConditions);
          setValue("remarks", response.data.remarks);
          setValue("leadId", response.data.leadId);
          // setValue("adjustments", response.data.adjustments);

          // Format dates
          setValue("date", dayjs(response.data.date).format("MM-DD-YYYY"));
          // setValue(
          //   "expiredate",
          //   dayjs(response.data.validTill).format("MM-DD-YYYY")
          // );

          // setSubtotal(response.data.subtotal);
          // setTotal(response.data.grandTotal);

          // Populate items
          if (response.data.quotationItems?.length) {
            const formattedItems = response.data.quotationItems.map(
              (item: any) => ({
                productId: item.productId,
                quantity: item.quantity.toString(),
                unit: item.unit,
                unitPrice: item.unitPrice.toString()
              })
            );

            replace(formattedItems); // Updates useFieldArray fields
          }
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      }
    };

    if (quatationId) fetchQuotation();
  }, [quatationId, setValue, replace]);
  const handleData = async (data: any) => {
    try {
      let request = {
        ownerId: localStorage.getItem("UserId"), // Include ownerId
        customerName: data?.customerName || "",
        address: data?.address || "",
        phone: data?.phone || "",
        stage: data?.stage || "Draft", // Defaulting to "Draft"
        rate: data?.rate || "",
        approximateArea: data?.approximateArea,
        date: dayjs(data.date).format("YYYY-MM-DDTHH:mm:ss.SSS"),
        commencement: dayjs(data.commencement).format(
          "YYYY-MM-DDTHH:mm:ss.SSS"
        ),
        completion: dayjs(data.completion).format("YYYY-MM-DDTHH:mm:ss.SSS"),
        termsAndConditions: data?.termsAndConditions || "",
        paymentTerms: data?.paymentTerms || "",
        remarks: data?.remarks || "demo",
        leadId: id || "",
        items: data?.items || []
      };

      setLoading(true)

      const newRequest = clearRequest(request);
      if (orderId) {
        const response: any = await callAPI('workorders', Method.PUT, newRequest,orderId);
        if (response) {
          showMessage(
            response?.message,
            response.success === true ? "success" : "error"
          );

          response.success === true && navigate(`/contact/edit/${id}`);
        }
      }
      else {
        const response: any = await callAPI('workorders', Method.POST, newRequest);
        if (response) {
          showMessage(
            response?.message,
            response.success === true ? "success" : "error"
          );

          response.success === true && navigate(`/contact/edit/${id}`);
        }
      }
    } catch (e: any) {
      showMessage(e.error);
    }
    finally {
      setLoading(false)
    }
  };

  const handleProductChange = (index: number, event: any) => {
    const selectedProductId = event.target.value; // Get selected product ID
    const selectedProduct = products.find(
      (p: any) => p.id === selectedProductId
    );
    if (selectedProduct) {
      setValue(`items.${index}.unit`, selectedProduct.unit);
      // setValue(`items.${index}.unitPrice`, selectedProduct.unitPrice);
    }
  };

  const handleCancelClick = () => {
    navigate(`/contact/edit/${id}`)
  };
  const actions: IHeaderListActions[] =
    [
      { type: 'button', text: 'Cancel', size: 'medium', onClick: handleCancelClick, variant: 'outlined', color: 'primary' },
      { type: 'button', text: 'Save', loading: loading, size: 'medium', onClick: handleSubmit(handleData), buttonType: 'submit', variant: 'outlined', color: 'primary' }
    ]

  return (
    <Grid p={2}>
      <form onSubmit={handleSubmit(handleData)}>
        <HeaderList title={orderId ? "Order > Update Order" : "Order > Create New Order"} type="card" actions={actions} isBackButton url={`/contact/edit/${id}`} />

        <Card
          sx={{ borderRadius: "10px", padding: 3, position: "relative", mt: 2 }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography fontWeight={"bold"}> Order</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <WSTextField
                label={"Customer Name *"}
                rules={{ required: true }}
                id={"customerName"}
                name={"customerName"}
                control={control}
              />
            </Grid>
            <Grid item xs={10} sm={6}>
              <WSPhone
                label={"Contact Number *"}
                id="phone"
                name="phone"
                control={control}
                rules={{ required: true }}
              />
            </Grid>
            {/* <Grid item xs={12} sm={2}>
              <WSIcons.SearchIcon />
            </Grid> */}
            <Grid item xs={10} sm={6}>
              <WSTextField
                label={"Address *"}
                // {...register("address")}
                id="address"
                name="address"
                control={control}
                rules={{ required: true }}
              />
            </Grid>



            <Grid item xs={10} sm={6}>
              <WSTextField
                label={"Rate *"}
                // {...register("rate")}
                id="rate"
                name="rate"
                control={control}
                rules={{ required: true }}
                type={TextType.Currency}
                currency={CurrencyType.Rupee}
              />
            </Grid>

            <Grid item xs={10} sm={6}>
              <WSTextField
                label={"Remarks *"}
                // {...register("remarks")}
                id="remarks"
                name="remarks"
                control={control}
                rules={{ required: true }}
              />
            </Grid>

            <Grid item xs={10} sm={6}>
              <WSSelectField
                rules={{ required: false }}
                // {...register("stage")}
                id="stage"
                control={control}
                defaultValue={"0"}
                label="Stage"
                name="stage"
                options={config.stageConfig.staticOptionData}
              />
            </Grid>

            <Grid item xs={10} sm={6}>
              <WSDateField
                id="date"
                name="date"
                label="Date *"
                control={control}
                rules={{ required: true }}
              />
            </Grid>

            <Grid item xs={10} sm={6}>
              <WSDateField
                id="commencement"
                name="commencement"
                label="Date of commencement *"
                control={control}
                rules={{ required: true }}
              />
            </Grid>
            <Grid item xs={10} sm={6}>
              <WSDateField
                id="completion"
                name="completion"
                label="Date of completion "
                control={control}
                rules={{ required: true }}
              />
            </Grid>

            {/* <Grid item xs={12} sm={6}>
              <WSSelectField
                id="leadId"
                label={"Select Lead"}
                name="leadId"
                control={control}
                rules={{ required: "please select a lead" }}
                options={lead.map((value: any) => ({
                  label: value.firstName,
                  value: value.id
                }))}
              />
            </Grid> */}

            <Grid item xs={12} sm={6}>
              <WSTextField
                id="area"
                name="approximateArea"
                label={"Approximate area *"}
                control={control}
                rules={{ required: true }}
              />
            </Grid>

            {/* <Grid item xs={10} sm={6}>
              <WSTextField
                label={"Period of Completion Work"}
                id={"period"}
                name={"period"}
                control={control}
                rules={{ required: false }}
              />
            </Grid> */}

            <Grid item xs={12} mt={2}>
              <Divider />
            </Grid>

            {/* Section Title */}
            <Grid item xs={12}>
              <Typography variant="h6">Items</Typography>
            </Grid>

            {/* Dynamic Item Fields */}
            {fields.map((row, index) => (
              <Grid
                container
                spacing={2}
                key={row.id}
                alignItems="center"
                mt={1}
                ml={1}
              >
                <Grid item xs={12} sm={3}>
                  <WSSelectField
                    label="Select a Product"
                    rules={{ required: true }}
                    {...register(`items.${index}.productId`)}
                    id={`selectProduct-${index}`}
                    control={control}
                    options={products.map((product: any) => ({
                      label: product.name,
                      value: product.id
                    }))}
                    onChange={(event) => handleProductChange(index, event)}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  <WSTextField
                    label="Unit"
                    {...register(`items.${index}.unit`)}
                    id={`unit-${index}`}
                    control={control}
                    disabled
                  />
                </Grid>
                {/* <Grid item xs={12} sm={3}>
                  <WSTextField
                    label="Unit Price"
                    {...register(`items.${index}.unitPrice`)}
                    id={`unitPrice-${index}`}
                    control={control}
                    type={TextType.Currency}
                    currency={CurrencyType.Rupee}
                  />
                </Grid> */}

                <Grid item xs={12} sm={2}>
                  <WSTextField
                    id={`quantity-${index}`}
                    label="Quantity"
                    {...register(`items.${index}.quantity`)}
                    control={control}
                    type={TextType.Number}
                  />
                </Grid>

                {/* Total Amount for Each Product */}
                {/* <Grid item xs={12} sm={2}>
                             <WSTextField
                               label="Total"
                               {...register(`items.${index}.total`)}
                               id={`total-${index}`}
                               control={control}
                               type={TextType.Currency}
                               currency={CurrencyType.Rupee}
                               disabled
                             />
                           </Grid> */}
                <Grid item xs={12} sm={2}>
                  <WSIcons.CloseIcon sx={{ cursor: 'pointer' }} onClick={() => remove(index)} />
                </Grid>
              </Grid>
            ))}

            {/* Add Item Button */}
            <Grid item xs={12} sx={{ marginLeft: 1 }}>
              <WSButton
                id="addButton"
                label="+ Add"
                size="small"
                onClick={() =>
                  append({
                    productId: "",
                    quantity: "",
                    unit: "",
                    // unitPrice: ""
                    // total: ""
                  })
                }
              />
            </Grid>

            {/* Terms & Conditions */}
            <Grid item xs={12} sm={12}>
              <WSTextAreaField
                id="termsandcondition"
                label="Terms and Conditions"
                {...register("termsAndConditions")}
                control={control}
                rows={3}
              />
            </Grid>
            {/* paymentTerms */}

            <Grid item xs={12} sm={12}>
              <WSTextAreaField
                id="paymentTerms"
                label="Payment Terms"
                {...register("paymentTerms")}
                control={control}
                rows={3}
              />
            </Grid>
          </Grid>
        </Card>
      </form>
    </Grid>
  );
});
